// The desktop's one-line IPC handlers, as CapturePoint's main process wrote them
// (apps/desktop/src/main/main.ts): renderer method -> engine command with its argument shape.
// Derived on 2026-09-18 by scratch/ipc-map.py; the thicker handlers live in desktop-adapter.ts.
/* eslint-disable @typescript-eslint/no-explicit-any, max-lines-per-function */
export type EngineCall = <T>(command: string, args?: Record<string, unknown>) => Promise<T>;

/** The desktop's guard: a job id is a non-empty string of sane length. */
function validateJobId(id: unknown): string {
  if (typeof id !== 'string' || id.trim().length === 0 || id.length > 256)
    throw new Error('A job id is required.');
  return id;
}

/** The desktop's guard for a Delete or a Restore: a stored batch and between 1 and 1,000 documents of it. */
function deletionRequest(request: any): { jobId: string; batchId: string; documentIds: string[] } {
  const jobId = validateJobId(request?.jobId);
  const batchId = request?.batchId;
  if (
    typeof batchId !== 'string' ||
    !batchId.trim() ||
    batchId.length > 256 ||
    batchId.startsWith('live:')
  )
    throw new Error('A document can be deleted or restored only once its batch is stored.');
  const documentIds = Array.isArray(request.documentIds)
    ? request.documentIds.filter(
        (id: unknown): id is string =>
          typeof id === 'string' && id.trim().length > 0 && id.length <= 256,
      )
    : [];
  if (documentIds.length === 0 || documentIds.length > 1000)
    throw new Error('Name between 1 and 1,000 documents to delete or restore.');
  return { jobId, batchId, documentIds };
}

/** The desktop's page of a job's Deleted view: newest first, at most 200 at a time, one kind of delete when named. */
function deletedListRequest(request: any): Record<string, unknown> {
  const whole = (value: unknown, least: number): number | null =>
    Number.isInteger(value) && (value as number) >= least ? (value as number) : null;
  const offset = whole(request?.offset, 0) ?? 0;
  const limit = Math.min(whole(request?.limit, 1) ?? 50, 200);
  const kind = request?.deletedBy;
  const deletedBy = kind === 'person' || kind === 'learned-junk' ? { deletedBy: kind } : {};
  return { jobId: validateJobId(request?.jobId), offset, limit, ...deletedBy };
}

export function thinCommands(
  call: EngineCall,
): Record<string, (...args: any[]) => Promise<unknown>> {
  return {
    autoTuneReviewBars: (jobId: any) => call('auto-tune-review-bars', { jobId }),
    batchHasCorrections: (request: any) =>
      call('batch-has-corrections', { jobId: request.jobId, batchId: request.batchId }),
    decideJobSuggestion: (request: any) =>
      call('decide-job-suggestion', {
        jobId: request.jobId,
        suggestionId: request.suggestionId,
        suggestionDecision: request.decision,
        snoozedUntil: request.snoozedUntil,
      }),
    deleteDataTable: (request: any) =>
      call('delete-data-table', {
        jobId: validateJobId(request.jobId),
        dataTableId: request.dataTableId,
      }),
    renameDataTable: (request: any) =>
      call('rename-data-table', {
        jobId: validateJobId(request.jobId),
        dataTableId: request.dataTableId,
        dataTableName: request.name,
      }),
    deleteLookupRule: (request: any) =>
      call('delete-lookup-rule', {
        jobId: validateJobId(request.jobId),
        lookupRuleId: request.ruleId,
      }),
    dismissBatchWarning: (request: any) =>
      call('dismiss-batch-warning', {
        jobId: request.jobId,
        batchId: request.batchId,
        query: request.warning,
      }),
    getBatchReprocessImpact: (request: any) =>
      call('batch-reprocess-impact', { jobId: request.jobId, batchId: request.batchId }),
    getJobSpeedHistory: (jobId: any) => call('job-speed-history', { jobId }),
    // `job-work-summary` is deliberately absent, and refused in not-carried.ts: PORT.md row 1
    // classifies CapturePoint's engine-side counts desktop-only, because Paige already answers
    // them from the ledger (JobCards.cs) in the tenant's own time zone. Asking the engine too
    // would put two sources of truth behind one card.
    getLatestBatchOverview: (jobId: any) => call('latest-batch-overview', { jobId }),
    getLearningAcceleration: (jobId: any) => call('learning-acceleration', { jobId }),
    // CPv6 dd5914a4: what the documents list's hints quote, from the engine's correction ledger.
    getReviewOutcomes: (jobId: any) => call('review-outcomes', { jobId: validateJobId(jobId) }),
    getLearningEffort: (jobId: any) => call('learning-effort', { jobId }),
    getLearningProgress: (jobId: any) => call('learning-progress', { jobId }),
    getLearningScorecard: (jobId: any) => call('learning-scorecard', { jobId }),
    // The Learning history asks for its own window (CPv6 getOperatorCorrections' limit); the engine's default is 100.
    getOperatorCorrections: (jobId: any, limit?: number) =>
      call('list-operator-corrections', limit === undefined ? { jobId } : { jobId, limit }),
    getDocumentTypeNames: (jobId: any) => call('document-type-names', { jobId }),
    getReviewImpactProfile: (jobId: any) => call('review-impact-profile', { jobId }),
    interpretField: (request: any) =>
      call('interpret-field', { fieldKind: request.kind, query: request.value }),
    jobAccuracy: (jobId: any) => call('job-accuracy', { jobId }),
    learnFieldFaults: (jobId: any) => call('learn-field-faults', { jobId }),
    learningEligibility: (jobId: any, batchId?: any) =>
      call('learning-eligibility', { jobId, batchId }),
    teachingRecommendations: (jobId: any, batchId: any) =>
      call('teaching-recommendations', { jobId, batchId }),
    learningFieldResults: (jobId: any) => call('learning-field-results', { jobId }),
    learningHistory: (jobId: any) => call('learning-history', { jobId }),
    learningStatus: (jobId: any) => call('learning-status', { jobId }),
    // The training session (L-01, 2026-10-07). Paige starts one by itself when a job's confirmations have moved
    // and Cortexa is free; Start here asks for one now under the same rules (the host says why when it cannot),
    // and Stop stops the one running. Both are a manager's (EngineCommandRoles).
    startLearning: (jobId: any) => call('start-learning', { jobId, learningMode: 'manual' }),
    // Calibration, the switch under Teach in Documents (CapturePoint bf1aed4c): on starts it again from now, off
    // ends it; the engine answers with the job as stored. A manager's (EngineCommandRoles).
    setCalibration: (jobId: any, on: any) => call('set-calibration', { jobId, calibrationOn: on }),
    stopLearning: (jobId: any) =>
      call('stop-learning', { jobId, query: 'Stopped from the Learning page' }),
    learningTableResults: (jobId: any) => call('learning-table-results', { jobId }),
    listJobSuggestionHistory: (jobId: any) =>
      call('list-job-suggestion-history', { jobId, limit: 30 }),
    listJobSuggestions: (jobId: any) => call('list-job-suggestions', { jobId }),
    listSelfAppliedSuggestions: (jobId: any) =>
      call('list-self-applied-suggestions', { jobId, limit: 5 }),
    removePage: (request: any) =>
      call('remove-page', {
        jobId: request.jobId,
        batchId: request.batchId,
        documentId: request.documentId,
        position: request.pageNumber,
        correction: request.correction,
      }),
    restorePage: (request: any) =>
      call('restore-page', {
        jobId: request.jobId,
        batchId: request.batchId,
        documentId: request.documentId,
        pages: [request.page],
        correction: request.correction,
      }),
    // Delete, Restore and the job's Deleted view (CapturePoint ADR-0096), the desktop's own shapes
    // (local-coordinator.ts). Delete and Restore are review writes the API fences and follows into the ledger
    // (ReviewDeletionMirror): a deleted document leaves review, the counts and the export, and Restore brings it back.
    deleteDocuments: (request: any) => call('delete-documents', deletionRequest(request)),
    restoreDocuments: (request: any) => call('restore-documents', deletionRequest(request)),
    listDeletedDocuments: (request: any) => call('deleted-documents', deletedListRequest(request)),
    // Turn page (CapturePoint e77212b7): the engine turns a copy of the page image, reads the page and the
    // document's values again, and answers the document as stored. One call on the job's channel; the API
    // fences it as a review write (a Reviewer's) and copies the turned page to the ledger (TurnedPageMirror).
    turnPage: (request: any) =>
      call('turn-page', {
        jobId: request.jobId,
        batchId: request.batchId,
        documentId: request.documentId,
        position: request.pageNumber,
        quarterTurns: request.quarterTurns,
        ...(request.documentType ? { documentTypes: [request.documentType] } : {}),
      }),
    saveCorrection: (request: any) =>
      call('save-correction', {
        jobId: request.jobId,
        batch: request.batch,
        correction: request.correction,
      }),
    suggestDocumentType: (request: any) =>
      call('suggest-document-type', {
        jobId: request.jobId,
        batchId: request.batchId,
        query: request.documentId,
      }),
    undoJobSuggestion: (request: any) =>
      call('undo-job-suggestion', { jobId: request.jobId, suggestionId: request.suggestionId }),
  };
}
